import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldAlert,
  Users,
  AlertOctagon,
  Eye,
  Clock,
} from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import StatTile from "@/components/bankruptcy/StatTile";
import ActiveFilingsTable from "@/components/bankruptcy/ActiveFilingsTable";
import StayExplainerCard from "@/components/bankruptcy/StayExplainerCard";
import PostBankruptcyOutcomes from "@/components/bankruptcy/PostBankruptcyOutcomes";
import LookbackPanel from "@/components/bankruptcy/LookbackPanel";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { relativeTime } from "@/lib/dates";

const BankruptcyWatchPage = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const [debtors, setDebtors] = useState([]);

  const loadPortfolio = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/bankruptcy/portfolio");
      setSnapshot(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadDebtors = async () => {
    try {
      const { data } = await apiClient.get("/debtors");
      setDebtors((data.debtors || []).filter((d) => d.status !== "archived"));
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    loadPortfolio();
    loadDebtors();
  }, []);

  const markReviewed = async (filing) => {
    const key = `${filing.debtor_id}:${filing.case_number}`;
    setMarking(key);
    try {
      await apiClient.post("/bankruptcy/reviews", {
        debtor_id: filing.debtor_id,
        case_number: filing.case_number,
      });
      toast.success(`Marked reviewed: ${filing.debtor_name}`);
      await loadPortfolio();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarking(null);
    }
  };

  const activeCount = snapshot?.active_filings_count ?? 0;
  const unreviewed = snapshot?.unreviewed_count ?? 0;
  const totalMonitored = snapshot?.total_debtors_monitored ?? 0;
  const lastSweep = snapshot?.last_sweep;
  const filings = snapshot?.active_filings ?? [];

  return (
    <div className="space-y-6" data-testid="bankruptcy-watch-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="space-y-1">
          <div className="jea-caption">Enforcement HALT Monitor</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">
            Bankruptcy <span className="text-accent-pink">Watch</span>
          </h1>
          <p className="text-text-secondary text-[14px] max-w-[720px]">
            The moment a debtor files, enforcement must stop. This dashboard scans your PACER
            query history for active filings, surfaces the automatic stay warning, and gives
            you a paper trail for review.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="bankruptcy-stats">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[12px]" />)
        ) : (
          <>
            <StatTile
              label="Judgments Monitored"
              value={totalMonitored}
              Icon={Users}
              testid="stat-monitored"
            />
            <StatTile
              label="Active Filings"
              value={activeCount}
              tone={activeCount > 0 ? "danger" : "default"}
              Icon={ShieldAlert}
              testid="stat-active-filings"
            />
            <StatTile
              label="Unreviewed"
              value={unreviewed}
              tone={unreviewed > 0 ? "warning" : "success"}
              sublabel={unreviewed > 0 ? "Requires acknowledgement" : "All clear"}
              Icon={AlertOctagon}
              testid="stat-unreviewed"
            />
            <StatTile
              label="Last PACER Sweep"
              value={lastSweep ? relativeTime(lastSweep) : "—"}
              sublabel={lastSweep ? "Aggregated from your PACER queries" : "Run a PACER query to populate"}
              Icon={Clock}
              testid="stat-last-sweep"
            />
          </>
        )}
      </div>

      {/* Stay Explainer — always shown, but pulses when there's an unreviewed filing */}
      {(activeCount > 0 || loading) && <StayExplainerCard />}

      {/* Active filings table (or empty state) */}
      {loading ? (
        <JeaCard className="min-h-[220px] flex items-center justify-center">
          <div className="text-text-secondary text-[13px]">Loading portfolio…</div>
        </JeaCard>
      ) : (
        <ActiveFilingsTable
          filings={filings}
          onMarkReviewed={markReviewed}
          marking={marking}
        />
      )}

      {/* Post-bankruptcy outcomes */}
      <PostBankruptcyOutcomes />

      {/* Lookback tool */}
      <LookbackPanel debtors={debtors} />
    </div>
  );
};

export default BankruptcyWatchPage;
